'use client'

import { Mail } from 'lucide-react'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { gmailWebUrl, planGmailOpen } from '@/lib/auth/openGmail'

// How long iOS gets to switch to the Gmail app before we assume it is not installed.
const IOS_APP_WAIT_MS = 1500

/**
 * Shown when /api/auth/otp reported a Google-hosted mailbox: one tap to the
 * inbox. A phone opens the Gmail app (the web inbox when it is missing); a
 * computer opens a new tab, so this page and its code box stay put.
 */
export default function OpenGmailButton({ email }: { email: string }) {
  const { t } = useLanguage()

  const open = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const plan = planGmailOpen(navigator.userAgent, navigator.maxTouchPoints ?? 0, email)
    if (plan.kind === 'tab') return // the link's own new-tab behaviour
    e.preventDefault()
    if (plan.kind === 'navigate') {
      window.location.href = plan.url
      return
    }
    // The app took over if the page went to the background.
    const timer = window.setTimeout(() => {
      if (document.visibilityState === 'visible') window.location.href = plan.webUrl
    }, IOS_APP_WAIT_MS)
    const cancel = () => {
      if (document.visibilityState === 'hidden') window.clearTimeout(timer)
    }
    document.addEventListener('visibilitychange', cancel, { once: true })
    window.location.href = plan.appUrl
  }

  return (
    <a
      href={gmailWebUrl(email)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={open}
      className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-gray-200 bg-white px-4 py-3 font-semibold text-gray-800 hover:bg-gray-50"
    >
      <Mail className="h-5 w-5 text-red-500" aria-hidden="true" />
      {t('authModal.openGmail')}
    </a>
  )
}
